import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wordmark } from "@/components/Brand";

const NAV_ITEMS = [
  { id: "inicio", label: "Inicio", type: "anchor" },
  { id: "experiencia", label: "Experiencia", type: "anchor" },
  { id: "sesiones", label: "Sesiones", type: "anchor" },
  { id: "extras", label: "Servicios", type: "anchor" },
  { id: "diario", label: "Diario", type: "route", to: "/diario" },
  { id: "instagram", label: "Instagram", type: "anchor" },
  { id: "sobre-mi", label: "Sobre mí", type: "anchor" },
  { id: "contacto", label: "Contacto", type: "anchor" },
];

export default function Nav({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (item) => {
    setOpen(false);
    if (item.type === "route") {
      navigate(item.to);
      return;
    }
    if (!onHome) {
      // navigate to home and scroll after mount
      navigate("/", { state: { scrollTo: item.id } });
      setTimeout(() => {
        const el = document.getElementById(item.id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
      return;
    }
    const el = document.getElementById(item.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !onHome ? "bg-cerenza-cream/90 backdrop-blur-xl shadow-[0_6px_24px_-18px_rgba(220,45,63,0.4)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-5 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <Wordmark className="h-9 md:h-10" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => handleClick(item)}
              className="uppercase text-[12px] tracking-[0.18em] font-semibold text-cerenza-ink/80 hover:text-cerenza-cherry transition-colors"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://www.instagram.com/cerenza_familyphotoroom"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-btn"
            aria-label="Instagram @cerenza_familyphotoroom"
            className="h-11 w-11 inline-flex items-center justify-center rounded-full text-cerenza-cherry hover:bg-cerenza-pink/40 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
            </svg>
          </a>
          <button
            data-testid="nav-book-btn"
            onClick={onBook}
            className="btn-cherry text-[11px] py-3 px-6"
          >
            Reserva tu sesión
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="menu"
          className="lg:hidden flex flex-col gap-1.5 items-center justify-center h-11 w-11 text-cerenza-cherry"
        >
          <span className={`block h-[2px] w-6 bg-current rounded transition-transform duration-300 ${open ? "translate-y-[6px] rotate-45" : ""}`} />
          <span className={`block h-[2px] w-6 bg-current rounded transition-opacity duration-300 ${open ? "opacity-0" : "opacity-100"}`} />
          <span className={`block h-[2px] w-6 bg-current rounded transition-transform duration-300 ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
        </button>
      </div>

      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden bg-cerenza-cream border-t border-cerenza-pink/40 transition-[max-height] duration-500 ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-3">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              className="text-left font-display text-2xl text-cerenza-ink hover:text-cerenza-cherry transition-colors"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="mobile-book-btn"
            onClick={() => { setOpen(false); onBook?.(); }}
            className="btn-cherry mt-2 self-start"
          >
            Reserva tu sesión
          </button>
        </div>
      </div>
    </header>
  );
}
